import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Cog, Link2, ChevronDown, ChevronUp, Hash } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AssetContext (admin) — surfaces the linked asset + property-level assets
 * directly in the WO drawer. Designed for ≤3-tap technician workflow:
 *   1) WO opens → linked asset is already visible (if linked)
 *   2) Optional: tap "Link by serial" → type SN → auto-link if 1 match
 *   3) Optional: tap "View all assets" → progressive disclosure
 */
const AssetContext = ({ workOrderId, onLinked }) => {
  const [ctx, setCtx] = useState(null);
  const [serial, setSerial] = useState('');
  const [linking, setLinking] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const load = () => axios.get(`${API}/admin/pm/work-orders/${workOrderId}/asset-context`, { withCredentials: true }).then((r) => setCtx(r.data));
  useEffect(() => { if (workOrderId) load(); /* eslint-disable-next-line */ }, [workOrderId]);

  const linkBySerial = async () => {
    if (!serial.trim()) return;
    setLinking(true);
    try {
      const { data } = await axios.post(`${API}/admin/pm/work-orders/${workOrderId}/link-asset`, { serial_number: serial.trim() }, { withCredentials: true });
      if (data.matched) {
        toast.success(`${data.auto ? 'Auto-linked' : 'Linked'} to ${data.asset.asset_type}`);
        setSerial(''); setSuggestions([]);
        load(); onLinked && onLinked(data.asset);
      } else {
        setSuggestions(data.suggestions || []);
        toast(`No exact match — choose from ${data.suggestions?.length || 0} suggestion${data.suggestions?.length === 1 ? '' : 's'}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Link failed');
    }
    setLinking(false);
  };

  const linkById = async (assetId) => {
    setLinking(true);
    try {
      const { data } = await axios.post(`${API}/admin/pm/work-orders/${workOrderId}/link-asset`, { asset_id: assetId }, { withCredentials: true });
      if (data.matched) {
        toast.success(`Linked to ${data.asset.asset_type}`);
        setSerial(''); setSuggestions([]);
        load(); onLinked && onLinked(data.asset);
      }
    } catch (e) {
      toast.error('Link failed');
    }
    setLinking(false);
  };

  if (!ctx) return null;
  const linked = ctx.linked_asset;
  const others = (ctx.property_assets || []).filter((a) => !linked || a.id !== linked.id);

  return (
    <div data-testid="asset-context" className="bg-[#141414] border border-[#262626] rounded-md p-4 space-y-3">
      <p className="text-[#D4AF37] text-[10px] font-bold tracking-[0.22em] uppercase flex items-center gap-2">
        <Cog className="w-3.5 h-3.5" /> Asset Context
      </p>

      {/* Linked asset — surfaces first per progressive disclosure */}
      {linked ? (
        <div data-testid="asset-context-linked" className="bg-[#0A0A0A] border border-emerald-700/40 rounded p-3">
          <p className="text-emerald-300 text-[10px] uppercase tracking-wider mb-1">Linked Asset</p>
          <p className="text-white text-sm font-medium">{linked.asset_type}{linked.unit_number ? ` · Unit ${linked.unit_number}` : ''}</p>
          <p className="text-[#A3A3A3] text-xs">{linked.manufacturer} {linked.model}{linked.serial_number ? ` · SN ${linked.serial_number}` : ''}</p>
          {linked.location_notes && <p className="text-[#525252] text-xs mt-1">📍 {linked.location_notes}</p>}
          {linked.service_history?.length > 0 && (
            <p className="text-[#A3A3A3] text-xs mt-2 flex items-center gap-1"><Link2 className="w-3 h-3" /> {linked.service_history.length} prior service{linked.service_history.length === 1 ? '' : 's'}</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-[#A3A3A3] text-xs">No asset linked yet — enter the unit's serial number for instant linkage:</p>
          <div className="flex gap-2">
            <input
              data-testid="asset-context-serial-input"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && linkBySerial()}
              placeholder="Serial or asset tag"
              className="flex-1 bg-[#0A0A0A] border border-[#262626] rounded h-9 px-3 text-white text-sm font-mono"
            />
            <button data-testid="asset-context-link-btn" disabled={linking || !serial.trim()} onClick={linkBySerial} className="bg-[#D4AF37] text-[#0A0A0A] h-9 px-3 rounded text-sm font-medium hover:bg-[#F3E5AB] disabled:opacity-40">
              {linking ? '…' : 'Link'}
            </button>
          </div>
          {suggestions.length > 0 && (
            <div className="bg-[#0A0A0A] border border-[#D4AF37]/40 rounded p-2 text-xs">
              <p className="text-[#D4AF37] mb-1">Pick the right one:</p>
              <ul className="space-y-1">
                {suggestions.map((s) => (
                  <li key={s.id}>
                    <button onClick={() => linkById(s.id)} className="text-left text-white hover:text-[#D4AF37] w-full">
                      {s.asset_type}{s.unit_number ? ` · #${s.unit_number}` : ''} <span className="text-[#525252]">— SN {s.serial_number || s.asset_tag || '—'}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Progressive disclosure — all property assets behind expand */}
      {others.length > 0 && (
        <div>
          <button data-testid="asset-context-toggle" onClick={() => setExpanded((v) => !v)} className="text-xs text-[#A3A3A3] hover:text-white inline-flex items-center gap-1">
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {others.length} other asset{others.length === 1 ? '' : 's'} at this property
          </button>
          {expanded && (
            <ul className="mt-2 space-y-1 text-xs">
              {others.map((a) => (
                <li key={a.id} className="flex items-center justify-between gap-2 py-1.5 border-t border-[#1f1f1f]">
                  <div className="min-w-0">
                    <p className="text-white truncate">{a.asset_type}{a.unit_number ? ` · #${a.unit_number}` : ''}</p>
                    <p className="text-[#525252] text-[10px] truncate">{a.manufacturer} {a.model} {a.serial_number && `· SN ${a.serial_number}`}</p>
                  </div>
                  {!linked && <button data-testid={`asset-context-link-${a.id}`} onClick={() => linkById(a.id)} className="text-[10px] uppercase tracking-wider text-[#D4AF37] hover:text-[#F3E5AB]">Link</button>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default AssetContext;
